import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { DatePicker } from "@/components/ui/DatePicker";
import { formatDate } from "@/utils/formatDate";
import { sickLeavesApi } from "../api/leaves";
import { useCreateSickLeave } from "../hooks/useLeaves";

interface Props {
  open: boolean;
  onClose: () => void;
  employee: { id: string; name: string };
}

/**
 * Lets an L4 verifier file a sick leave on behalf of `employee`. Like a timesheet
 * card filed on behalf, it lands in the pending queue (to be checked in ZUS) and is
 * attributed to the verifier via `submitted_by` — unless the verifier ticks that
 * the L4 is already confirmed in ZUS, which registers it as verified straight away.
 */
export function ProxySickLeaveSubmitModal({ open, onClose, employee }: Props) {
  const createSickLeave = useCreateSickLeave();

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [note, setNote] = useState("");
  const [verifyNow, setVerifyNow] = useState(false);
  const [comment, setComment] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: existingRes } = useQuery({
    queryKey: ["sick-leaves", "employee", employee.id],
    queryFn: () => sickLeavesApi.list({ employee: employee.id }),
    enabled: open,
  });

  // The backend refuses an L4 overlapping one the employee already has (a rejected
  // one does not count) — surface it before submitting.
  const overlapping = useMemo(() => {
    if (!startDate || !endDate || endDate < startDate) return [];
    return (existingRes?.data ?? []).filter(
      (sl) => sl.status !== "rejected" && sl.start_date <= endDate && sl.end_date >= startDate,
    );
  }, [existingRes, startDate, endDate]);

  const daysPreview = useMemo(() => {
    if (!startDate || !endDate || endDate < startDate) return null;
    return (new Date(endDate).getTime() - new Date(startDate).getTime()) / 86_400_000 + 1;
  }, [startDate, endDate]);

  const reset = () => {
    setStartDate("");
    setEndDate("");
    setNote("");
    setVerifyNow(false);
    setComment("");
    setErrors({});
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const submit = () => {
    const e: Record<string, string> = {};
    if (!startDate) e.startDate = "Wymagane";
    if (!endDate) e.endDate = "Wymagane";
    if (startDate && endDate && endDate < startDate) e.endDate = "Data końcowa nie może być przed datą początkową";
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setErrors({});

    createSickLeave.mutate(
      {
        employee: employee.id,
        start_date: startDate,
        end_date: endDate,
        note,
        verify_now: verifyNow,
        verification_comment: verifyNow ? comment : "",
      },
      {
        onSuccess: () => { handleClose(); },
        onError: (err: unknown) => {
          const detail = (err as { response?: { data?: { errors?: { detail?: string } } } })
            ?.response?.data?.errors?.detail;
          setErrors({ submit: detail ?? "Błąd podczas zgłaszania L4. Spróbuj ponownie." });
        },
      },
    );
  };

  return (
    <Modal open={open} onClose={handleClose} title={`Złóż L4 — ${employee.name}`}>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Data od <span className="text-red-500">*</span>
            </label>
            <DatePicker value={startDate} onChange={setStartDate} />
            {errors.startDate && <p className="text-xs text-red-500 mt-1">{errors.startDate}</p>}
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Data do <span className="text-red-500">*</span>
            </label>
            <DatePicker value={endDate} min={startDate} onChange={setEndDate} />
            {errors.endDate && <p className="text-xs text-red-500 mt-1">{errors.endDate}</p>}
          </div>
        </div>
        {daysPreview !== null && (
          <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
            Łącznie: <strong>{daysPreview}</strong> {daysPreview === 1 ? "dzień" : "dni"} kalendarzowych
          </p>
        )}
        {overlapping.length > 0 && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
            Pracownik ma już zgłoszone L4 w tym okresie:
            <ul className="mt-1 list-disc pl-4">
              {overlapping.map((sl) => (
                <li key={sl.id}>
                  {formatDate(sl.start_date)} – {formatDate(sl.end_date)} ({sl.status_display.toLowerCase()})
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Uwagi <span className="text-gray-400 text-xs">(opcjonalne)</span>
          </label>
          <textarea
            rows={2}
            className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Np. numer zaświadczenia lekarskiego..."
          />
        </div>

        <div>
          <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
            <input
              type="checkbox"
              className="w-4 h-4 rounded accent-[var(--color-primary)]"
              checked={verifyNow}
              onChange={(e) => setVerifyNow(e.target.checked)}
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">L4 jest już potwierdzone w ZUS</span>
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {verifyNow
              ? "Zgłoszenie zostanie od razu zarejestrowane jako zweryfikowane."
              : "Zgłoszenie trafi do oczekujących — zweryfikujesz je w ZUS jak każde inne."}
          </p>
          {verifyNow && (
            <textarea
              rows={2}
              className="mt-2 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Komentarz weryfikatora (opcjonalny), np. ZUS-ZLA nr 12345..."
            />
          )}
        </div>

        {errors.submit && <p className="text-sm text-red-600">{errors.submit}</p>}

        <div className="flex gap-3">
          <Button loading={createSickLeave.isPending} onClick={submit} disabled={overlapping.length > 0}>
            <Stethoscope size={14} className="mr-1.5" /> Złóż L4
          </Button>
          <Button variant="secondary" onClick={handleClose}>Anuluj</Button>
        </div>
      </div>
    </Modal>
  );
}
